// Revenue schedules: revenue schedules that recognize an invoice's total
// period by period. Recognizing the next period posts DR deferred revenue /
// CR the revenue account and rolls the schedule forward, completing it at
// the last period.
import { formatMoney } from "@sylviq/ui-core";
import { Button } from "@sylviq/ui-react-web/components/ui/button";
import { Drawer } from "@sylviq/ui-react-web/components/ui/drawer";
import { Input } from "@sylviq/ui-react-web/components/ui/input";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import {
  StatusDot,
  type StatusTone,
} from "@sylviq/ui-react-web/components/ui/status-dot";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@sylviq/ui-react-web/components/ui/table";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { Link } from "@tanstack/react-router";
import {
  type FormEvent,
  useCallback,
  useEffect,
  useState,
} from "react";
import { InvoicePicker } from "@/components/Pickers";
import { CardField } from "@/components/RecordCard";
import {
  clearDocumentDraft,
  emptyDraftLine,
  loadDocumentDraft,
  newDocumentDraft,
  saveDocumentDraft,
  type DocumentDraft,
} from "@/features/document-lines/documentDraft";
import {
  createRevenueSchedule,
  listRevenueSchedules,
  REVENUE_METHODS,
  type RevenueSchedule,
} from "@/lib/api";
import { humanizeLabel, useInvoiceNumbers } from "@/lib/labels";
import { useOrganization } from "@/lib/organization";

const REVENUE_SCHEDULE_DRAFT_PREFIX =
  "niche-accounting.revenue-schedule-draft.v1";

function statusTone(status: string): StatusTone {
  switch (status.toLowerCase()) {
    case "completed":
      return "success";
    case "active":
      return "info";
    case "cancelled":
      return "danger";
    default:
      return "idle";
  }
}

function plainStatus(status: string): string {
  const lower = status.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
}

function toErrorMessage(cause: unknown): string {
  return cause instanceof Error ? cause.message : String(cause);
}

function toMinor(amount: string): number | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  const value = Number(trimmed);
  if (!Number.isFinite(value) || value <= 0) return null;
  const minor = Math.round(value * 100);
  return Number.isSafeInteger(minor) && minor > 0 ? minor : null;
}

function todayInput(): string {
  return new Date().toISOString().slice(0, 10);
}

export function RevenueSchedulesPage() {
  const { scope } = useOrganization();
  const organizationId = scope?.organization_id ?? "default";

  const [schedules, setSchedules] = useState<RevenueSchedule[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Creation draft, mirrored per organization (see documentDraft). The mirror's
  // `crmContactId` slot carries the selected source invoice id and the first
  // line's `amount` the total to spread — the generic draft has no dedicated
  // source-document or headline-amount field. Start date, end date and
  // method stay in page state next to the mirror.
  const [draft, setDraft] = useState<DocumentDraft>(newDocumentDraft);
  const [loadedDraftKey, setLoadedDraftKey] = useState<string | null>(null);
  const [startDate, setStartDate] = useState(todayInput);
  const [endDate, setEndDate] = useState(todayInput);
  const [method, setMethod] = useState<string>("straight_line");
  const [creatorOpen, setCreatorOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Client-side table controls over the fetched list (the list endpoint has
  // no search/sort params — filtering happens here).
  const [statusTab, setStatusTab] = useState("");
  const [search, setSearch] = useState("");

  const invoiceNumbers = useInvoiceNumbers();

  const reload = useCallback(() => {
    setLoading(true);
    setError(null);
    listRevenueSchedules()
      .then(setSchedules)
      .catch((cause: unknown) => setError(toErrorMessage(cause)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  // Draft mirror: load from this browser session when the organization is known,
  // then save on every change. A failed storage read yields a fresh draft.
  useEffect(() => {
    setDraft(
      loadDocumentDraft(REVENUE_SCHEDULE_DRAFT_PREFIX, organizationId) ??
        newDocumentDraft(),
    );
    setLoadedDraftKey(organizationId);
  }, [organizationId]);
  useEffect(() => {
    if (loadedDraftKey !== organizationId) return;
    saveDocumentDraft(REVENUE_SCHEDULE_DRAFT_PREFIX, organizationId, draft);
  }, [draft, loadedDraftKey, organizationId]);

  const updateDraft = (patch: Partial<DocumentDraft>) =>
    setDraft((prev) => ({ ...prev, ...patch }));

  const total = draft.lines[0]?.amount ?? "";
  const updateTotal = (value: string) =>
    setDraft((prev) => ({
      ...prev,
      lines:
        prev.lines.length === 0
          ? [{ ...emptyDraftLine(), amount: value }]
          : prev.lines.map((line, index) =>
              index === 0 ? { ...line, amount: value } : line,
            ),
    }));

  const clearDraft = () => {
    setDraft(newDocumentDraft());
    setStartDate(todayInput());
    setEndDate(todayInput());
    setMethod("straight_line");
    clearDocumentDraft(REVENUE_SCHEDULE_DRAFT_PREFIX, organizationId);
  };

  const today = todayInput();
  const hasDraft =
    draft.crmContactId !== "" ||
    total.trim() !== "" ||
    startDate !== today ||
    endDate !== today ||
    method !== "straight_line";

  const openCreator = () => {
    setCreateError(null);
    setCreatorOpen(true);
  };

  const statusCounts = (schedules ?? []).reduce<Record<string, number>>(
    (counts, schedule) => {
      const key = schedule.status.toLowerCase();
      counts[key] = (counts[key] ?? 0) + 1;
      return counts;
    },
    {},
  );
  const statusTabs = Object.keys(statusCounts).sort();

  const visibleSchedules = (schedules ?? [])
    .filter(
      (schedule) => !statusTab || schedule.status.toLowerCase() === statusTab,
    )
    .filter((schedule) => {
      const query = search.trim().toLowerCase();
      if (!query) return true;
      return (
        (invoiceNumbers.get(schedule.invoice_id) ?? "").toLowerCase().includes(
          query,
        ) || schedule.method.toLowerCase().includes(query)
      );
    });

  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setSubmitting(true);
    setCreateError(null);
    try {
      if (!draft.crmContactId) throw new Error("Pick the invoice to spread out.");
      const total_minor = toMinor(total);
      if (total_minor === null)
        throw new Error("Total must be a positive number (e.g. 12.50).");
      if (!startDate || !endDate)
        throw new Error("Start and end dates are required.");
      if (endDate < startDate)
        throw new Error("The end date must be on or after the start date.");
      await createRevenueSchedule({
        invoice_id: draft.crmContactId,
        total_minor,
        start_date: startDate,
        end_date: endDate,
        method,
      });
      clearDraft();
      setCreatorOpen(false);
      reload();
    } catch (cause) {
      setCreateError(toErrorMessage(cause));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="grid gap-6">
      <PageHeader
        title="Revenue schedules"
        description="Recognize invoice income a little at a time — each period moves its share from deferred to earned."
        actions={
          <Button type="button" onClick={openCreator}>
            New schedule
          </Button>
        }
      />

      {error ? (
        <div
          role="alert"
          className="surface rounded-xl border border-[var(--danger)] p-4 text-sm text-[var(--danger)]"
        >
          {error}
          <div className="mt-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={reload}
            >
              Retry
            </Button>
          </div>
        </div>
      ) : null}

      {error && schedules === null ? null : (
        <div className="grid gap-3">
          <Tabs
            label="Filter revenue schedules by status"
            tabs={[
              { value: "", label: `All (${(schedules ?? []).length})` },
              ...statusTabs.map((status) => ({
                value: status,
                label: `${plainStatus(status)} (${statusCounts[status]})`,
              })),
            ]}
            value={statusTab}
            onValueChange={setStatusTab}
          >
            <div className="surface grid gap-3 rounded-2xl p-4">
              <div className="flex flex-wrap items-end gap-3">
                <label
                  htmlFor="revenue-schedules-search"
                  className="grid min-w-40 flex-1 gap-1 text-xs font-medium"
                >
                  Search
                  <Input
                    id="revenue-schedules-search"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    placeholder="Invoice number or method…"
                    disabled={loading}
                  />
                </label>
                <Button
                  type="button"
                  variant="secondary"
                  onClick={reload}
                  disabled={loading}
                >
                  {loading ? "Loading…" : "Refresh"}
                </Button>
              </div>
            </div>

            <div
              className="surface hidden overflow-hidden rounded-2xl min-[64rem]:block"
              data-testid="revenue-schedules-table"
            >
              <div className="table-scroll">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Invoice</TableHead>
                      <TableHead>Period</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Recognized</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loading && schedules === null ? (
                      <TableRow>
                        <TableCell colSpan={5}>Loading…</TableCell>
                      </TableRow>
                    ) : (schedules ?? []).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No revenue schedules yet — create the first one above.
                        </TableCell>
                      </TableRow>
                    ) : visibleSchedules.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5}>
                          No revenue schedules match these filters.
                        </TableCell>
                      </TableRow>
                    ) : (
                      visibleSchedules.map((schedule) => (
                        <TableRow key={schedule.id}>
                          <TableCell className="font-mono text-xs">
                            <Link
                              to="/revenue-schedules/$revenueScheduleId"
                              params={{ revenueScheduleId: schedule.id }}
                              className="hover:underline"
                              title={schedule.id}
                            >
                              {invoiceNumbers.get(schedule.invoice_id) ??
                                "Schedule"}
                            </Link>
                          </TableCell>
                          <TableCell className="whitespace-nowrap text-xs">
                            {new Date(schedule.start_date).toLocaleDateString()}{" "}
                            → {new Date(schedule.end_date).toLocaleDateString()}
                          </TableCell>
                          <TableCell>
                            <StatusDot
                              tone={statusTone(schedule.status)}
                              label={plainStatus(schedule.status)}
                            />
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: schedule.recognized_minor,
                              currency: "USD",
                            })}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatMoney({
                              cents: schedule.total_minor,
                              currency: "USD",
                            })}
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>

            <div
              className="stack-list grid gap-3 min-[64rem]:hidden"
              data-testid="revenue-schedules-stack"
            >
              {loading && schedules === null ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  Loading…
                </p>
              ) : (schedules ?? []).length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No revenue schedules yet — create the first one above.
                </p>
              ) : visibleSchedules.length === 0 ? (
                <p className="text-sm text-[var(--muted-foreground)]">
                  No revenue schedules match these filters.
                </p>
              ) : (
                visibleSchedules.map((schedule) => (
                  <article
                    key={schedule.id}
                    className="stack-card rounded-xl border border-[var(--border)] bg-[var(--muted)] p-4"
                  >
                    <div className="stack-card-heading flex flex-wrap items-center justify-between gap-4 text-sm">
                      <Link
                        to="/revenue-schedules/$revenueScheduleId"
                        params={{ revenueScheduleId: schedule.id }}
                        className="record-title underline-offset-4 hover:underline"
                      >
                        {invoiceNumbers.get(schedule.invoice_id) ?? "Schedule"}
                      </Link>
                      <span className="tabular-nums">
                        {formatMoney({
                          cents: schedule.total_minor,
                          currency: "USD",
                        })}
                      </span>
                    </div>
                    <CardField label="Period">
                      {new Date(schedule.start_date).toLocaleDateString()} →{" "}
                      {new Date(schedule.end_date).toLocaleDateString()}
                    </CardField>
                    <CardField label="Status">
                      <StatusDot
                        tone={statusTone(schedule.status)}
                        label={plainStatus(schedule.status)}
                      />
                    </CardField>
                    <CardField label="Recognized">
                      {formatMoney({
                        cents: schedule.recognized_minor,
                        currency: "USD",
                      })}
                    </CardField>
                    <div className="flex flex-wrap gap-2 pt-3">
                      <Button asChild variant="secondary" size="sm">
                        <Link
                          to="/revenue-schedules/$revenueScheduleId"
                          params={{ revenueScheduleId: schedule.id }}
                        >
                          Open
                        </Link>
                      </Button>
                    </div>
                  </article>
                ))
              )}
            </div>
          </Tabs>
        </div>
      )}

      <Drawer
        open={creatorOpen}
        onOpenChange={setCreatorOpen}
        title="New revenue schedule"
        description="The total must fit inside the invoice total. Monthly periods are worked out from the date range. Closing this panel keeps your draft in this browser session."
        className="w-[min(46rem,100%)]"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => setCreatorOpen(false)}
              disabled={submitting}
            >
              Back to schedules
            </Button>
            {hasDraft ? (
              <Button
                type="button"
                variant="ghost"
                onClick={clearDraft}
                disabled={submitting}
              >
                Discard draft
              </Button>
            ) : null}
            <Button
              type="submit"
              form="revenue-schedule-create-form"
              disabled={submitting}
              className="ms-auto"
            >
              {submitting ? "Creating…" : "Create schedule"}
            </Button>
          </>
        }
      >
        <form
          id="revenue-schedule-create-form"
          onSubmit={submit}
          className="grid gap-3"
          aria-label="Create revenue schedule"
        >
          {createError ? (
            <div
              role="alert"
              className="rounded-xl border border-[var(--danger)] p-3 text-sm text-[var(--danger)]"
            >
              {createError}
            </div>
          ) : null}
          <label htmlFor="rs-invoice" className="grid gap-1 text-xs font-medium">
            Invoice
            <InvoicePicker
              id="rs-invoice"
              value={draft.crmContactId}
              onChange={(invoiceId) =>
                updateDraft({ crmContactId: invoiceId })
              }
              disabled={submitting}
              required
            />
          </label>
          <label htmlFor="rs-total" className="grid gap-1 text-xs font-medium">
            Total to spread (USD)
            <Input
              id="rs-total"
              type="number"
              value={total}
              onChange={(e) => updateTotal(e.target.value)}
              required
              placeholder="0.00"
              step="0.01"
              min="0"
              inputMode="decimal"
              disabled={submitting}
            />
          </label>
          <div className="grid gap-3 sm:grid-cols-2">
            <label htmlFor="rs-start" className="grid gap-1 text-xs font-medium">
              Starts
              <Input
                id="rs-start"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                required
                disabled={submitting}
              />
            </label>
            <label htmlFor="rs-end" className="grid gap-1 text-xs font-medium">
              Ends
              <Input
                id="rs-end"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                required
                disabled={submitting}
              />
            </label>
          </div>
          <label htmlFor="rs-method" className="grid gap-1 text-xs font-medium">
            How to split it
            <select
              id="rs-method"
              className="select"
              value={method}
              onChange={(e) => setMethod(e.target.value)}
              disabled={submitting}
              title="Recognition method (technical term)"
            >
              {REVENUE_METHODS.map((option) => (
                <option key={option} value={option}>
                  {humanizeLabel(option)}
                </option>
              ))}
            </select>
          </label>
        </form>
      </Drawer>
    </div>
  );
}
